import React, { useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";

import NotFound from "core/NotFound";
import SectionContainer from "core/SectionContainer";
import CreatorProfileNav from "./CreatorProfileNav";
import CreatorProfilePhotos from "./CreatorProfilePhotos";
import ProfileHeader from "core/ProfileHeader";
import PageMeta from "core/PageMeta";
import CreatorProfileIconsPage from "./CreatorProfileIconsPage";

import { GET_USER_DATA } from "frontend/components/public-profile/queries";
import { PUBLIC_PROFILE } from "data/links";

export const PAGE_CONTENT_TYPES = {
    PHOTOS: "photos",
    ICONS: "icons"
};

const CreatorProfilePage = ({ creatorData = {}, username }) => {
    const [pageContent, setPageContent] = useState();

    const { data: totalsData } = useQuery(GET_USER_DATA, {
        variables: {
            username
        }
    });

    if (!creatorData) return <NotFound />;

    const {
        twitter,
        facebook,
        behance,
        dribbble,
        linkedin,
        instagram,
        description
    } = creatorData;

    const totalIcons =
        totalsData && totalsData.icons && totalsData.icons.totalCount;
    const isIconCreator = !!totalIcons;

    const totalPhotos =
        totalsData && totalsData.photos && totalsData.photos.totalCount;
    const isPhotoCreator = !!totalPhotos;

    let content = null;

    if (pageContent === PAGE_CONTENT_TYPES.ICONS)
        content = <CreatorProfileIconsPage username={username} />;
    if (pageContent === PAGE_CONTENT_TYPES.PHOTOS)
        content = <CreatorProfilePhotos username={username} />;

    const pageUrl = `${window.location.origin}${PUBLIC_PROFILE.replace(
        ":username",
        encodeURIComponent(username)
    )}`;

    const getSocialLinks = () => {
        const links = [];

        if (twitter) links.push(`https://twitter.com/${twitter}`);
        if (facebook) links.push(`https://www.facebook.com/${facebook}`);
        if (behance) links.push(`https://www.behance.net/${behance}`);
        if (dribbble) links.push(`https://dribbble.com/${dribbble}`);
        if (linkedin) links.push(`https://www.linkedin.com/${linkedin}`);
        if (instagram) links.push(`https://www.instagram.com/${instagram}`);

        return links;
    };

    let pageTitle = "";
    let pageDescription = "";

    if (isIconCreator && isPhotoCreator) {
        pageTitle = `Free Images by ${creatorData.name} - Noun Project`;
        pageDescription = `Browse ${totalIcons +
            totalPhotos} royalty free icons and photos by ${
            creatorData.name
        } at Noun Project. Find the perfect free pictures for your next project.`;
    } else if (isPhotoCreator) {
        pageTitle = `Free Photos by ${creatorData.name} - Noun Project`;
        pageDescription = `Browse ${totalPhotos} royalty free images by ${creatorData.name} at Noun Project. Find the perfect stock photos for your next project.`;
    } else {
        pageTitle = `Free Icons by ${creatorData.name} - Noun Project`;
        pageDescription = `Browse ${totalIcons} free vector and PNG icons by ${creatorData.name} at Noun Project. Find the perfect royalty free images for your next project.`;
    }

    return (
        <>
            <PageMeta
                title={pageTitle}
                canonicalURL={pageUrl}
                description={pageDescription}
                useSocialMediaTags
                previewImg={creatorData.avatarUrl}
                previewImgAlt={`${creatorData.name} avatar`}
                indexContent="index,follow,max-image-preview:large"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "Person",
                    title: pageTitle,
                    name: creatorData.name,
                    image: creatorData.avatarUrl,
                    sameAs: getSocialLinks(),
                    description: description || pageDescription,
                    url: pageUrl
                }}
            />
            <ProfileHeader username={username} />
            <SectionContainer
                topSpacing="none"
                data-testid="creator-profile-page"
            >
                <CreatorProfileNav
                    setPageContent={setPageContent}
                    hasPublishedIcons={isIconCreator}
                    hasPublishedPhotos={isPhotoCreator}
                />
                {content}
            </SectionContainer>
        </>
    );
};

CreatorProfilePage.propTypes = {
    creatorData: PropTypes.object,
    username: PropTypes.string.isRequired
};

export default CreatorProfilePage;
